// src/components/Dashboard.jsx  --  Digital Me App Shell

import React, { useState, useCallback, useEffect } from 'react';
import './Dashboard.css';
import AccountsListPage from './AccountsListPage';
import AccountDetailPage from './AccountDetailPage';
import ResearchPage from './ResearchPage';
import AITeamStrip from './AITeamStrip';
import LeadsPage from './LeadsPage';
import PipelinePage from './PipelinePage';
import TasksPage from './TasksPage';
import { apiFetch } from '../lib/api';
import CommandCenter from './CommandCenter';
import JarvisPage from './JarvisPage';
import VictorPage from './VictorPage';
import CyranoPage from './CyranoPage';
import TheFloor from './TheFloor';
import TodayPage from './TodayPage';
import logoSrc from '../assets/Main-logo-UI.png';
import ProfileSettingsPage from './ProfileSettingsPage';
import AdminPage from './AdminPage';
import { useProfile } from '../context/UserProfileContext';
import { useAuth } from '../context/AuthContext';
// P0.21 task 21.10f — the global readiness banner and the role rule it shares
// with Settings. ELEVATED_ROLES is imported rather than re-declared: there are
// already two copies (ProfileSettingsPage and api/shared/auth-context.js) and a
// third is how the three drift.
import ReadinessBanner from './ReadinessBanner';
import { ELEVATED_ROLES } from './ProfileSettingsPage';
import ContactsPage from './ContactsPage';
import MeetingPage from './MeetingPage';
import CommsPage from './CommsPage';
import CalendarPage from './CalendarPage';

import QuinnPage from './QuinnPage';
import CompetitorFeedPage from './CompetitorFeedPage';
import WatchlistPage from './WatchlistPage';
import SignalsPage from './SignalsPage';
import LegalLinks from '../legal/LegalLinks';

const NAV_ITEMS = [
  {
    section: 'Sales',
    items: [
      { id: 'accounts',    icon: '🏢', label: 'Accounts' },
      { id: 'contacts',    icon: '👥', label: 'Contacts' },
      { id: 'leads',       icon: '🎯', label: 'Leads',       badge: 'leads' },
      { id: 'pipeline',    icon: '📊', label: 'Pipeline' },
      { id: 'tasks',       icon: '✅', label: 'Tasks',       badge: 'tasks' },
    ]
  },
  {
    section: 'Intelligence',
    items: [
      { id: 'calendar',    icon: '🗓️', label: 'Calendar' },
      { id: 'comms',       icon: '💬', label: 'Comms',         badge: 'email' },
      { id: 'signals',     icon: '📡', label: 'Signals',     badge: 'signals' },
      { id: 'market-intel',icon: '🏁', label: 'Market Intel',badge: 'competitors' },
      { id: 'watchlist',   icon: '👁️', label: 'Watchlist' },
      // Victor and Morning Plan were listed here briefly so they could be
      // tested; both have since moved to the AI strip, which is where the
      // noun/verb rule puts them — they are sessions that produce something,
      // not collections you browse. See docs/navigation-model.md.
    ]
  },
  {
    section: 'Settings',
    items: [
      { id: 'profile',     icon: '👤', label: 'Profile & Settings' },
      // P0.34 — platform administration, moved out of Profile & Settings. It
      // sits under Settings rather than in a section of its own: it IS a
      // setting-shaped destination, and a fourth nav section for one item would
      // make the sidebar read as though administration were a peer of Sales.
      //
      // `systemAdminOnly` is read by the render below. The flag lives on the
      // item rather than the section so that the section label still renders
      // for everyone (everyone has Profile & Settings) while this one entry
      // disappears — a section that vanishes entirely for most users is a
      // bigger visual change than one item that does.
      //
      // HIDING IS A COURTESY. Every endpoint behind this page re-checks its own
      // gate; see AdminPage.jsx's header for what the page does and does not
      // enforce.
      { id: 'admin',       icon: '🛡️', label: 'Administration', systemAdminOnly: true },
    ]
  }
];


// Page titles for the persistent topbar, derived from the same NAV_ITEMS the
// sidebar renders so a renamed nav entry cannot leave a stale title behind.
// The AI-strip destinations are not in NAV_ITEMS and are listed explicitly.
// Short forms for the topbar on narrow viewports. Only the destinations whose
// full name does not fit are listed; everything else falls through to
// NAV_LABELS unchanged. Rendered as a second span rather than swapped in JS so
// the choice stays a CSS-breakpoint decision, not a resize listener.
const NAV_LABELS_SHORT = {
  dashboard: 'ICC',
  'market-intel': 'Market',
  profile: 'Settings',
  'meeting-from-account': 'Meeting',
};

const NAV_LABELS = {
  ...Object.fromEntries(NAV_ITEMS.flatMap(s => s.items.map(i => [i.id, i.label]))),
  dashboard: 'Intelligence Command Center',
  today:     'Today',
  quinn:     'Quinn',
  floor:     'The Floor',
  research:  'Research',
  content:   'The Studio',
  jarvis:    'Jarvis',
  meetings:  'Meeting',
  'meeting-from-account': 'Meeting',
  'account-detail': 'Account',
};

export default function Dashboard() {
  const { firstName, companyName, avatarInitial } = useProfile();
  const { logout, role } = useAuth();
  // 21.10f. Gated on the same rule as the Settings banner — "do not show it to
  // someone who cannot act on it". A member seeing "complete these 5 settings"
  // for a screen they cannot open is worse than silence, and /api/tenant/profile
  // 403s them anyway (requireRole), so the fetch is skipped rather than failed.
  const isElevated = ELEVATED_ROLES.includes(role);
  const [readiness, setReadiness] = useState(null);
  // Deep-link from an OAuth return: the callback sends the browser to
  // `/?auth_success=…#profile/Integrations`. There is no router here — this component
  // switches on `currentView` — so the landing view is read from the fragment ONCE, in
  // the initialiser rather than an effect, to avoid rendering the dashboard for a frame
  // and then jumping. Anything unrecognised falls through to 'dashboard'.
  const [currentView, setCurrentView] = useState(() => {
    const view = (window.location.hash || '').replace(/^#/, '').split('/')[0];
    // P0.34 — `admin` joins `profile` as a fragment-addressable landing view.
    // Both are spelled out rather than allow-listing every route, because an
    // unrecognised fragment must fall through to the dashboard: a typo in a
    // shared link should not render a blank switch default.
    if (view === 'profile') return 'profile';
    if (view === 'admin')   return 'admin';
    return 'dashboard';
  });
  // The segment AFTER the view, read once for the same reason the view is:
  // `#admin/Platform` must open on Platform without rendering Users for a frame.
  const [initialHashSection] = useState(() => {
    const parts = (window.location.hash || '').replace(/^#/, '').split('/');
    return parts.length > 1 ? decodeURIComponent(parts.slice(1).join('/')) : null;
  });
  // P0.34 — whether to offer the Administration nav entry. Resolved from the
  // server because system admin is an address in SYSTEM_ADMIN_EMAILS, a Vercel
  // variable, not a role in the JWT — there is nothing in the token to read.
  // /api/me/data-request answers it without probing an admin endpoint for every
  // user on every page load.
  //
  // Defaults to FALSE, so the entry is absent until the probe says otherwise:
  // an item that appears a moment after load is a smaller surprise than one
  // that appears and then vanishes. A failed probe leaves it false, which is
  // the safe failure — and the page itself refuses anyone who reaches #admin
  // directly, so the menu is the courtesy and not the control.
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/me/data-request')
      .then(r => r.json())
      .then(d => { if (!cancelled) setIsSystemAdmin(Boolean(d.isSystemAdmin)); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [badges, setBadges] = useState({
    leads: 0, tasks: 0, competitors: 0, signals: 0, email: 0
  });

  // ── Jarvis → Leads deep-link ─────────────────────────────────────────────
  // When Jarvis "Open Full Lead Card" is clicked, these get set.
  // LeadsPage reads them, auto-opens that specific card, then clears them.
  const [openLeadId,   setOpenLeadId]   = useState(null);
  const [openLeadData, setOpenLeadData] = useState(null);
  // AI deep-link context — set when navigating to Victor/Cyrano from an opportunity card
  const [aiNavContext, setAiNavContext] = useState(null);

  useEffect(() => {
    async function fetchBadges() {
      try {
        const [leadsRes, tasksRes, inboxRes] = await Promise.all([
          apiFetch('/api/leads/list'),
          apiFetch('/api/tasks/list'),
          apiFetch('/api/comms/inbox?filter=all&status=unread'),
        ]);
        const leadsData = await leadsRes.json();
        const tasksData = await tasksRes.json();
        const inboxData = await inboxRes.json();
        const activeLeads = (leadsData.leads || []).filter(l => l.status !== 'Archived').length;
        const activeTasks = (tasksData.tasks || []).filter(t => t.status !== 'Done').length;
        const unreadEmails = (inboxData.emails || inboxData.items || []).length;
        setBadges(prev => ({ ...prev, leads: activeLeads, tasks: activeTasks, email: unreadEmails }));
      } catch (err) {
        console.error('Badge fetch error:', err);
      }
    }
    fetchBadges();
  }, []);

  // 21.10f. The same assessment Settings shows, fetched once for the shell so
  // the gap is visible outside the screen where it is fixed. Computed by the
  // API (api/shared/tenant-readiness.js) so the rule has ONE definition — a
  // second copy in JSX is a copy that drifts.
  useEffect(() => {
    if (!isElevated) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/api/tenant/profile');
        if (!res.ok) return;              // 403 for a member is expected, not an error
        const json = await res.json();
        if (!cancelled) setReadiness(json.readiness || null);
      } catch {
        // Silent on purpose: a banner that cannot load is a banner that is not
        // shown. Failing loudly here would put an error on every page for a
        // hint the user did not ask for.
      }
    })();
    return () => { cancelled = true; };
  }, [isElevated]);



  const navigate = useCallback((view, accountId = null, context = null) => {
    // 'floor' view accepts a full context object directly as the third param
    setCurrentView(view);
    if (accountId) setSelectedAccountId(accountId);
    // context = { accountId, accountName, opportunityId, opportunityName, mode }
    // Used for deep-linking Victor/Cyrano with pre-loaded opportunity
    if (context) {
      setAiNavContext(context);
      // 'content' is the live Cyrano route. A duplicate 'cyrano' case was
      // removed 30 Aug 2026 (P1 3.5a.3) on the stated grounds that "nothing
      // navigated to" it — which was FALSE when written: three buttons did,
      // in TodayPage, PipelinePage and OpportunitiesTab. They were not found
      // because the check looked for the route, not its callers. All three
      // then fell through to `default:` and dumped the user on this dashboard,
      // silently — no error, no warning, the context object simply discarded —
      // until P1.5 IA-1a repointed them at 'content' on 19 Sep 2026.
      //
      // tests/navigate-targets-resolve.test.js now asserts every navigate()
      // target string has a case, so the next one cannot ship. Do not restore
      // a 'cyrano' route: the id is dead on purpose and the buttons are right.
    } else if (view !== 'victor' && view !== 'content' && view !== 'floor' && view !== 'quinn') {
      setAiNavContext(null); // clear context when navigating away from AI pages
    } else if (view === 'floor' && !context) {
      setAiNavContext(null); // fresh floor open from nav clears prior context
    }
    window.scrollTo(0, 0);
  }, []);

  // Expose navigate globally for standalone pages (PipelinePage, etc.)
  useEffect(() => {
    window.__dmNavigate = navigate;
    return () => { window.__dmNavigate = null; };
  }, [navigate]);

  // Called by JarvisPage when user clicks "Open Full Lead Card"
  const handleOpenLead = useCallback((leadId, leadData) => {
    setOpenLeadId(leadId);
    setOpenLeadData(leadData);
    setCurrentView('leads');
    window.scrollTo(0, 0);
  }, []);

  // Called by LeadsPage after it has opened the modal — clears state
  const handleLeadOpened = useCallback(() => {
    setOpenLeadId(null);
    setOpenLeadData(null);
  }, []);

  const renderPage = () => {
    if (currentView === 'account-detail' && selectedAccountId) {
      return (
        <AccountDetailPage
          accountId={selectedAccountId}
          onBack={() => navigate('accounts')}
          onNavigateToAccount={(id) => navigate('account-detail', id)}
          onNavigate={navigate}
          openOpportunityId={aiNavContext?.openOpportunityId || null}
        />
      );
    }
    switch (currentView) {
      case 'dashboard':   return <CommandCenter onNavigate={navigate} badges={badges} />;
      case 'jarvis':      return <JarvisPage onNavigate={navigate} onOpenLead={handleOpenLead} />;
      case 'floor':       return <TheFloor onNavigate={navigate} entryContext={aiNavContext || {}} returnTo={aiNavContext?.returnTo || null} />;
      case 'quinn':       return <QuinnPage onNavigate={navigate} />;
      case 'today':       return <TodayPage onNavigate={navigate} />;
      case 'victor':      return <VictorPage onNavigate={navigate} preloadedContext={aiNavContext} />;
      case 'accounts':    return <AccountsListPage onSelectAccount={(id) => navigate('account-detail', id)} />;
      case 'contacts':    return <ContactsPage onNavigate={navigate} />;
      case 'meetings':    return <MeetingPage onNavigate={navigate} />;
      case 'comms':       return <CommsPage onNavigate={navigate} onUnreadChange={(count) => setBadges(prev => ({ ...prev, email: count }))} />;
      // Deep link to Comms' Drafts tab. Mirrors 'content' -> CyranoPage: a route
      // id that pins a tab, rather than teaching navigate() a fourth argument.
      // MeetingPage's "Review Drafts →" is the caller (P1.5 IA-1c).
      case 'comms-drafts': return <CommsPage onNavigate={navigate} initialTab="drafts" onUnreadChange={(count) => setBadges(prev => ({ ...prev, email: count }))} />;
      case 'meeting-from-account': return <MeetingPage onNavigate={navigate} presetAccountId={selectedAccountId} presetAccountName={aiNavContext?.accountName || null} />;
      case 'leads':       return (
                            <LeadsPage
                              openLeadId={openLeadId}
                              openLeadData={openLeadData}
                              onLeadOpened={handleLeadOpened}
                              onNavigate={navigate}
                            />
                          );
      case 'pipeline':    return <PipelinePage onNavigate={navigate} />;
      case 'tasks':       return <TasksPage onNavigateAccount={(id) => navigate('account-detail', id)} />;
      case 'research':    return <ResearchPage onNavigate={navigate} />;
      case 'profile':     return <ProfileSettingsPage />;
      // P0.34 — platform administration, moved out of Profile & Settings. The
      // page gates itself on SYSTEM_ADMIN_EMAILS and explains the gate when it
      // refuses, so reaching this route without access is handled there rather
      // than by hiding the case.
      case 'admin':       return <AdminPage initialSection={initialHashSection} />;
      // NO initialTab, since 16 Oct 2026. The owner: "when i open it, the first
      // thing i see is victor… the right most is the one opened by default."
      // The Studio opened on Content Studio — the SIXTH of six tabs — because
      // the route id `content` collides with a TAB id `content`, and this line
      // passed the route straight through as the tab.
      //
      // `comms-drafts -> initialTab="drafts"` above is the correct shape of the
      // same idea: a DISTINCT route id deliberately landing on a named tab. This
      // only looked like that.
      //
      // CyranoPage defaults to 'compose' on its own — its first tab and the
      // page's primary verb. Passing nothing is what makes that default
      // reachable, and means the route id and the tab id cannot be confused.
      //
      // NOT PORTED HERE: base-app and augence also pass initialContext, so the
      // three ✍️ Cyrano buttons arrive with their account pre-filled. That is a
      // separate improvement this tree has not had, and adding it inside a fix
      // for the landing tab would be two changes in one line. Left alone
      // deliberately — tests/studio-lands-on-compose.test.js checks for it only
      // where it exists.
      case 'content':     return <CyranoPage onNavigate={navigate} />;
      case 'calendar':    return <CalendarPage onNavigate={navigate} />;

      case 'market-intel':return <CompetitorFeedPage />;
      case 'signals':     return <SignalsPage onNavigate={navigate} />;
      case 'watchlist':   return <WatchlistPage />;
      default:            return <CommandCenter onNavigate={navigate} badges={badges} />;
    }
  };

  return (
    <div className="dm-shell">
      <aside className="dm-sidebar">

        <div className="dm-brand" onClick={() => navigate('dashboard')}>
          <img src={logoSrc} className="dm-brand-logo" alt="Digital Me" />
          <div className="dm-brand-name">
            <div className="dm-brand-name-line dm-brand-name-line--digital">
              <span className="dm-brand-name-halo" aria-hidden="true">Digital</span>
              <span className="dm-brand-name-fill">Digital</span>
            </div>
            <div className="dm-brand-name-line dm-brand-name-line--me">
              <span className="dm-brand-name-halo" aria-hidden="true">Me</span>
              <span className="dm-brand-name-fill">Me</span>
            </div>
          </div>
        </div>

        {NAV_ITEMS.map(section => (
          <div key={section.section} className="dm-nav-section">
            <div className="dm-nav-section-label">{section.section}</div>
            {section.items.filter(item => !item.systemAdminOnly || isSystemAdmin).map(item => {
              const isActive   = currentView === item.id ||
                (item.id === 'accounts' && currentView === 'account-detail') ||
                (item.id === 'meetings' && currentView === 'meeting-from-account');
              const badgeCount = item.badge ? badges[item.badge] : null;
              return (
                <div
                  key={item.id}
                  className={`dm-nav-item ${isActive ? 'dm-nav-active' : ''}`}
                  onClick={() => navigate(item.id)}
                >
                  {isActive && <div className="dm-nav-indicator" />}
                  <span className="dm-nav-icon">{item.icon}</span>
                  <span className="dm-nav-label">{item.label}</span>
                  {/* The badge class keys on the nav item's ID, not its badge key.
                      There is no item with id 'email' — Comms carries
                      `badge: 'email'`, which is the COUNTER's name — so that branch
                      was dead long before the EmailPage route was removed
                      (P1.5 IA-3b) and Comms has always fallen through to green.
                      Dropped rather than "fixed": changing Comms to amber would be
                      a visual change nobody asked for, smuggled in under a
                      deletion. */}
                  {badgeCount > 0 && (
                    <span className={`dm-nav-badge ${
                      ['leads','tasks'].includes(item.id) ? 'dm-badge-red' :
                      item.id === 'intel'        ? 'dm-badge-blue' :
                      item.id === 'signals'      ? 'dm-badge-green' :
                      item.id === 'market-intel' ? 'dm-badge-purple' :
                      'dm-badge-green'
                    }`}>{badgeCount}</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        <div className="dm-user">
          <div className="dm-user-avatar">{avatarInitial}</div>
          <div className="dm-user-info">
            <div className="dm-user-name">{firstName}</div>
            <div className="dm-user-role">{companyName}</div>
          </div>
          <span className="dm-user-settings" onClick={() => navigate('profile')} title="Settings">⚙️</span>
        </div>
        <button className="dm-signout-btn" onClick={logout}>
          ↩ Sign Out
        </button>
        <LegalLinks className="dm-legal" />

      </aside>

      <main className="dm-main">
        <div className="dm-persistent-header">
          <div className="dm-persistent-topbar">
            <span className="dm-persistent-brand">Digital<span>Me</span></span>
            {/* The page title, not a fixed string. It read "Intelligence Command
                Center" on every screen including Accounts and Contacts, which
                told the user nothing and cost the widest element in the bar.
                NAV_LABELS derives from the same list the sidebar renders, so
                the two cannot drift apart. */}
            <span className="dm-persistent-title">{NAV_LABELS[currentView] || 'Intelligence Command Center'}</span>
            <span className="dm-persistent-title-short">{NAV_LABELS_SHORT[currentView] || NAV_LABELS[currentView] || 'ICC'}</span>
            <div className="dm-persistent-right">
              {/* "Updated 6:02 AM" was a hardcoded literal — it never changed,
                  so it asserted a freshness the app could not know. Removed
                  rather than wired up: the pages that HAVE a real timestamp
                  already show their own. */}
              <button className="dm-persistent-refresh" onClick={() => window.location.reload()} title="Reload">⟳<span className="dm-persistent-refresh-label"> Refresh</span></button>
            </div>
          </div>
          <AITeamStrip onNavigate={navigate} currentPage={currentView} />
        </div>
        {/* 21.10f. Between the header and the content so it is visible on every
            view, not only in Settings where the user already is. */}
        <ReadinessBanner readiness={readiness} onNavigate={navigate} />
        <div className="dm-page-content">
          {renderPage()}
        </div>
      </main>
    </div>
  );
}
